import { Mappings } from 'shared-libs/models/types/storyboard/storyboard-plan-model'
import { EditableFieldProps } from '../core-field/editable-field-json-props'
import {
  FIRE_DEFAULT_DEBOUNCE_MS,
  FireButtonVariant,
  FireFieldProps,
  FireInlineLookupProps,
  FireMode,
  ResolvableValue,
} from '../core-field/fire-field-props'

/**
 * Wider onChange signature, matching `ui2:apiField`. The renderer's entity-bind hook passes
 * optional `silent` (skip pipeline broadcast) and `overridePath` (write to a sibling path) flags on
 * some paths; the field forwards them verbatim so callers can bind through those channels.
 */
export type UI2EventFieldOnChange<T> = (
  value: T | undefined,
  silentUpdate?: boolean,
  overridePath?: string,
) => void

export type EventFireMode = FireMode
export type EventButtonVariant = FireButtonVariant

/**
 * A server event carries no HTTP status — a subscriber either succeeded or it didn't — so there is
 * no per-status retry tier to configure.
 */
export type EventRetryType = 'never' | 'onFailure'

/**
 * A single output mapping, matching the shape schema authors already write in a plan's
 * `serverEventHooks`. `value` is resolved on the client before the event is fired, so the server
 * receives a literal.
 */
export interface EventOutputMapping {
  name?: string
  /** JSONPath on the execution the resolved value is written to, without the leading `$.`. */
  destination: string
  value?: ResolvableValue<unknown>
}

export interface EventFieldResponse {
  success: boolean
  /**
   * Values read off the computed execution, keyed by the path that produced them — the `source` of
   * each `valueMappings` entry. Retained on the bound value so a schema author can read a result the
   * mappings didn't route anywhere, and so the debug panel can show what came back.
   */
  resolvedValues: Record<string, unknown>
  errorMsg: string | null
  /** Echoes whether the server ran this as a dry run, so a read-only render can say so. */
  dryRun: boolean
}

export const UI2_EVENT_FIELD_DEFAULT_PROPS: Partial<UI2EventFieldProps> = {
  fireMode: 'onClick',
  buttonLabel: 'Submit',
  retryType: 'never',
  dryRun: true,
  debounceMs: FIRE_DEFAULT_DEBOUNCE_MS,
}

export interface UI2EventFieldProps
  extends Omit<EditableFieldProps<EventFieldResponse>, 'onChange'>,
    FireFieldProps,
    FireInlineLookupProps {
  onChange?: UI2EventFieldOnChange<EventFieldResponse>

  /** The storyboard server event to fire, e.g. `core_yms_event_trailerLookup`. */
  eventType: string
  /**
   * Written to the event's `details`; each value may be a literal or an expression. These are
   * materialized by the renderer against the frames before the field is mounted, so they can
   * reference anything on the form — but nothing that exists only inside the component.
   */
  details?: Record<string, ResolvableValue<unknown>>

  /**
   * Same shape as `details`, but the `lazy` prefix keeps the renderer from materializing it, so the
   * field resolves the expressions itself with its own state in scope — notably `lookupQuery`, the
   * query submitted under `fireMode: 'inlineLookup'`. Expressions are resolved at any depth, so a
   * nested detail like `userLookup.phone` works.
   *
   * Deep-merged over `details`, so one field can mix form-driven and lookup-driven values.
   */
  lazyDetails?: Record<string, unknown>

  outputMappings?: EventOutputMapping[]
  /** `lazy` counterpart of `outputMappings`; appended to them, resolved with `lookupQuery` in scope. */
  lazyOutputMappings?: EventOutputMapping[]
  /**
   * Routes results off the computed execution into the form, using the same `Mapping` shape
   * `ui2:entitySearchField` and a plan's own mappings use.
   *
   * - `source` — path on the computed execution to read, e.g. `core_storyboard_execution.trailer.name`.
   *   The set of sources is what the server is asked to read back; a mapping with no `source` reads
   *   nothing and is resolved purely from its `formula`.
   * - `destination` — path in the form to write the value to. Unlike `entitySearchField`, whose
   *   destinations are relative to its own bound value, these are absolute form paths, so a mapping
   *   can write anywhere the schema author wants.
   * - `formula` — optional expression, with the read value in scope as `_srcValue` and the fired
   *   event's outcome as `eventResponse`. When absent the source value is written through as-is.
   *
   * A mapping whose resolved value is null writes nothing, matching `evaluateMappings`.
   */
  valueMappings?: Mappings
  /**
   * When true (the default) the event is computed but nothing is persisted — the resolved values
   * land in local form state and the form's own submit writes them. Set false to persist as soon as
   * the event is processed.
   *
   * A dry run is rejected by the server unless every subscriber for `eventType` accepts it for
   * these particular event details.
   */
  dryRun?: boolean

  retryType?: EventRetryType
}
